'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'
import { CircleAlert } from 'lucide-react'
import { Tooltip } from './tooltip'

interface TextAreaProps extends Omit<React.ComponentProps<'textarea'>, 'size'> {
  label?: string
  helpText?: string
  tooltip?: string
  error?: { message?: string } | boolean
  subLabel?: string
  labelClassName?: string
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      label,
      error,
      helpText,
      tooltip,
      subLabel,
      labelClassName,
      ...props
    },
    ref
  ) => {
    const inputId = React.useId()
    const errorMessage = typeof error === 'object' ? error?.message : undefined

    return (
      <div className='space-y-1'>
        {label && (
          <span className='flex items-center space-x-1'>
            <label
              htmlFor={props?.id || inputId}
              className={cn(
                'font-sans text-sm leading-4 text-neutral-500',
                labelClassName
              )}
            >
              {label}
            </label>
            {subLabel && (
              <span className='text-sm italic text-neutral-400'>
                {subLabel}
              </span>
            )}
            {tooltip && (
              <Tooltip content={tooltip}>
                <CircleAlert
                  className={'h-3.5 w-3.5 cursor-help stroke-neutral-500'}
                />
              </Tooltip>
            )}
          </span>
        )}
        <textarea
          className={cn(
            'flex w-full rounded-lg border border-input bg-background p-4 text-base font-normal text-neutral-800 transition file:border-0 file:bg-transparent file:font-medium file:text-foreground placeholder:font-normal placeholder:text-neutral-400 focus:border-neutral-500 focus-visible:outline-none enabled:hover:border-neutral-500 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-300',
            error &&
              'border-error-500 enabled:hover:border-error-500 enabled:focus:border-error-500',
            className
          )}
          rows={10}
          ref={ref}
          id={props?.id || inputId}
          {...props}
        />
        {(helpText || errorMessage) && (
          <span className='flex items-center'>
            <CircleAlert
              className={cn(
                'mr-1 h-3.5 w-3.5 fill-neutral-400 stroke-neutral-400 [&_line]:stroke-light',
                errorMessage &&
                  'fill-error-500 stroke-error-500 [&_line]:stroke-light'
              )}
            />
            <p
              className={cn(
                'font-sans text-xs leading-4 text-neutral-400',
                errorMessage && 'text-error-500'
              )}
            >
              {errorMessage || helpText}
            </p>
          </span>
        )}
      </div>
    )
  }
)
TextArea.displayName = 'TextArea'

export { TextArea }
// Alias for backward compatibility
export { TextArea as Textarea }
